import React, { useEffect, useId, useRef } from 'react'
import { startOfDay } from 'date-fns'

import { useAppTranslation } from '../../../hooks/useAppTranslation'

interface TimeColumnsProps {
  // react-datepicker injects these when it clones the element
  value?: string
  onChange?: (time: string) => void
  date?: Date
  // the DatePicker only blocks whole days, so the time floor is applied here
  minDate?: Date | null
}

const HOURS = Array.from({ length: 24 }, (_, hour) => hour)
const MINUTES = Array.from({ length: 60 }, (_, minute) => minute)

const pad = (unit: number): string => String(unit).padStart(2, '0')

// a non-number would come back out of pad() as "NaN"
const toUnit = (raw?: string): number => {
  const unit = Number(raw)
  return Number.isInteger(unit) ? unit : 0
}

// unlike scrollIntoView, this leaves the page scroll alone
const centerInColumn = (
  column: HTMLUListElement | null,
  row: HTMLLIElement | null
): void => {
  if (!column || !row) {
    return
  }
  column.scrollTo({
    top: row.offsetTop - column.clientHeight / 2 + row.clientHeight / 2,
  })
}

export const TimeColumns = ({
  value,
  onChange,
  date,
  minDate,
}: TimeColumnsProps): JSX.Element => {
  const { translate } = useAppTranslation()

  // unique ids, since two pickers can be open at once
  const headingId = useId()
  const hourHeadingId = `${headingId}-hour`
  const minuteHeadingId = `${headingId}-minute`

  // with nothing selected `date` is the epoch, so that also takes minDate's floor
  const floor =
    minDate && date && startOfDay(date) <= startOfDay(minDate)
      ? minDate
      : undefined
  const minHour = floor ? floor.getHours() : 0
  const minMinute = floor ? floor.getMinutes() : 0

  const [rawHour, rawMinute] = value ? value.split(':') : []
  const hour = rawHour === undefined ? minHour : toUnit(rawHour)
  const minute = rawMinute === undefined ? minMinute : toUnit(rawMinute)

  const hourColumn = useRef<HTMLUListElement>(null)
  const minuteColumn = useRef<HTMLUListElement>(null)
  const selectedHour = useRef<HTMLLIElement>(null)
  const selectedMinute = useRef<HTMLLIElement>(null)

  useEffect(() => {
    centerInColumn(hourColumn.current, selectedHour.current)
    centerInColumn(minuteColumn.current, selectedMinute.current)
  }, [])

  const hourDisabled = (item: number): boolean => item < minHour
  const minuteDisabled = (item: number): boolean =>
    hour <= minHour && item < minMinute

  const select = (nextHour: number, nextMinute: number): void => {
    if (nextHour < minHour) {
      return
    }
    // the floor hour with a lower minute would still land before the floor
    const clampedMinute =
      nextHour === minHour && nextMinute < minMinute ? minMinute : nextMinute
    onChange?.(`${pad(nextHour)}:${pad(clampedMinute)}`)
  }

  const rowClassName = (isSelected: boolean, isDisabled: boolean): string => {
    if (isDisabled) {
      return 'cursor-not-allowed px-3 py-1 text-center text-neutral-70'
    }
    return `cursor-pointer px-3 py-1 text-center ${
      isSelected ? 'bg-brandGreen text-white' : 'hover:bg-neutral-96'
    }`
  }

  return (
    <div className="boappa-time-columns">
      <div className="boappa-time-columns__column-wrap">
        <div className="boappa-time-columns__header" id={hourHeadingId}>
          {translate('common.hour')}
        </div>
        <ul
          className="boappa-time-columns__column"
          ref={hourColumn}
          role="listbox"
          aria-labelledby={hourHeadingId}
        >
          {HOURS.map((item) => {
            const isDisabled = hourDisabled(item)
            return (
              <li
                key={item}
                ref={item === hour ? selectedHour : undefined}
                className={rowClassName(item === hour, isDisabled)}
                onClick={isDisabled ? undefined : () => select(item, minute)}
                onKeyDown={(e) =>
                  !isDisabled && e.key === 'Enter' && select(item, minute)
                }
                role="option"
                aria-selected={item === hour}
                aria-disabled={isDisabled}
                tabIndex={isDisabled ? -1 : 0}
              >
                {pad(item)}
              </li>
            )
          })}
        </ul>
      </div>
      <div className="boappa-time-columns__column-wrap">
        <div className="boappa-time-columns__header" id={minuteHeadingId}>
          {translate('common.minute')}
        </div>
        <ul
          className="boappa-time-columns__column"
          ref={minuteColumn}
          role="listbox"
          aria-labelledby={minuteHeadingId}
        >
          {MINUTES.map((item) => {
            const isDisabled = minuteDisabled(item)
            return (
              <li
                key={item}
                ref={item === minute ? selectedMinute : undefined}
                className={rowClassName(item === minute, isDisabled)}
                onClick={isDisabled ? undefined : () => select(hour, item)}
                onKeyDown={(e) =>
                  !isDisabled && e.key === 'Enter' && select(hour, item)
                }
                role="option"
                aria-selected={item === minute}
                aria-disabled={isDisabled}
                tabIndex={isDisabled ? -1 : 0}
              >
                {pad(item)}
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}
